import { useEffect } from "react";
import { getRelatedTools, getHowToSteps } from "@/lib/tools/tool-config";

/**
 * Minimal head-side-effect helper for SEO meta tags. We use document.title
 * + document.head meta tags directly rather than pulling in react-helmet
 * to keep the bundle lean. Returns nothing — fires on mount + cleanup.
 */
export function useToolSeo({
    title,
    description,
    canonical,
    structuredData,
    ogImage,
}) {
    useEffect(() => {
        const prevTitle = document.title;
        if (title) document.title = title;

        const head = document.head;
        const setMeta = (name, content) => {
            if (!content) return null;
            let el = head.querySelector(`meta[name="${name}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                head.appendChild(el);
            }
            const prev = el.getAttribute("content");
            el.setAttribute("content", content);
            return { el, prev, created };
        };

        // Open Graph + Twitter tags use property=, not name=.
        const setOgMeta = (property, content) => {
            if (!content) return null;
            let el = head.querySelector(`meta[property="${property}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("property", property);
                head.appendChild(el);
            }
            const prev = el.getAttribute("content");
            el.setAttribute("content", content);
            return { el, prev, created };
        };

        const setLink = (rel, href) => {
            if (!href) return null;
            let el = head.querySelector(`link[rel="${rel}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement("link");
                el.setAttribute("rel", rel);
                head.appendChild(el);
            }
            const prev = el.getAttribute("href");
            el.setAttribute("href", href);
            return { el, prev, created };
        };

        const descMeta = setMeta("description", description);
        const canonicalLink = setLink("canonical", canonical);

        // Open Graph + Twitter Card tags — required for rich link previews
        // on Facebook, Twitter/X, LinkedIn, iMessage, Slack, Discord.
        const ogTitleMeta = setOgMeta("og:title", title);
        const ogDescMeta = setOgMeta("og:description", description);
        const ogUrlMeta = setOgMeta("og:url", canonical);
        const ogTypeMeta = setOgMeta("og:type", "website");
        const ogImageMeta = setOgMeta("og:image", ogImage);
        const ogImageAltMeta = ogImage ? setOgMeta("og:image:alt", title) : null;
        const twitterCardMeta = setMeta(
            "twitter:card", ogImage ? "summary_large_image" : "summary",
        );
        const twitterTitleMeta = setMeta("twitter:title", title);
        const twitterDescMeta = setMeta("twitter:description", description);
        const twitterImageMeta = setMeta("twitter:image", ogImage);

        // JSON-LD structured data
        let structuredScript = null;
        if (structuredData) {
            structuredScript = document.createElement("script");
            structuredScript.type = "application/ld+json";
            structuredScript.text = JSON.stringify(structuredData);
            head.appendChild(structuredScript);
        }

        return () => {
            document.title = prevTitle;
            const restore = (m) => {
                if (!m) return;
                if (m.created) m.el.remove();
                else m.el.setAttribute(m.el.hasAttribute("content") ? "content" : "href", m.prev || "");
            };
            [descMeta, ogTitleMeta, ogDescMeta, ogUrlMeta, ogTypeMeta,
             ogImageMeta, ogImageAltMeta, twitterCardMeta, twitterTitleMeta,
             twitterDescMeta, twitterImageMeta].forEach(restore);
            if (canonicalLink) {
                if (canonicalLink.created) canonicalLink.el.remove();
                else canonicalLink.el.setAttribute("href", canonicalLink.prev || "");
            }
            if (structuredScript) structuredScript.remove();
        };
    }, [title, description, canonical, ogImage, JSON.stringify(structuredData)]);
}

export function buildToolJsonLd({ tool, faqs = [] }) {
    const origin =
        (typeof window !== "undefined" && window.location?.origin) || "";
    const url = `${origin}/tools/${tool.slug}`;
    const howToSteps = getHowToSteps(tool);
    const relatedTools = getRelatedTools(tool, 3);
    return [
        {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: tool.name,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            url,
            description: tool.metaDescription,
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Tools",
                    item: `${origin}/tools`,
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: tool.name,
                    item: url,
                },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `How to use the ${tool.name}`,
            description: tool.metaDescription,
            totalTime: "PT3M",
            estimatedCost: {
                "@type": "MonetaryAmount",
                currency: "USD",
                value: "0",
            },
            step: howToSteps.map((s, idx) => ({
                "@type": "HowToStep",
                position: idx + 1,
                name: s.name,
                text: s.text,
                url: `${url}#step-${idx + 1}`,
            })),
        },
        relatedTools.length
            ? {
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  name: `Related tools to ${tool.name}`,
                  itemListElement: relatedTools.map((t, idx) => ({
                      "@type": "ListItem",
                      position: idx + 1,
                      url: `${origin}/tools/${t.slug}`,
                      name: t.name,
                  })),
              }
            : null,
        faqs.length
            ? {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: faqs.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
              }
            : null,
    ].filter(Boolean);
}
